import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Settings, 
  Percent, 
  Save, 
  Loader2,
  Check,
  Phone,
  AlertCircle,
  Star,
  Sparkles,
  Eye,
  EyeOff,
  Bell
} from "lucide-react";
import { toast } from "sonner";
import { useBusiness } from "@/contexts/BusinessContext";
import NotificationSettings from "./NotificationSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsTab() {
  const { currentBusiness, businessId } = useBusiness();
  const [interestRate, setInterestRate] = useState(20);
  const [originalRate, setOriginalRate] = useState(20);
  const [phone, setPhone] = useState("55 1318 1745");
  const [originalPhone, setOriginalPhone] = useState("55 1318 1745");
  const [whatsapp, setWhatsapp] = useState("5539835137");
  const [originalWhatsapp, setOriginalWhatsapp] = useState("5539835137");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Banner state
  const [banner, setBanner] = useState({
    enabled: true,
    title: "Ofertas Especiales",
    subtitle: "Encuentra tu vehículo ideal con las mejores condiciones de financiamiento",
    cta_text: "Ver Ofertas",
    background_color: "gradient"
  });
  const [originalBanner, setOriginalBanner] = useState(null);
  const [savingBanner, setSavingBanner] = useState(false);

  // Featured vehicles
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  useEffect(() => {
    fetchConfig();
    fetchBanner();
    fetchVehicles();
  }, [businessId]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config?business_id=${businessId}`);
      if (response.data.success) {
        const config = response.data.config;
        const rate = config.interest_rate || 20;
        setInterestRate(rate);
        setOriginalRate(rate);
        
        // Cargar teléfonos
        const configPhone = config.phone || currentBusiness?.phone || "55 1318 1745";
        const configWhatsapp = config.whatsapp || currentBusiness?.whatsapp || "5539835137";
        setPhone(configPhone);
        setOriginalPhone(configPhone);
        setWhatsapp(configWhatsapp);
        setOriginalWhatsapp(configWhatsapp);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBanner = async () => {
    try {
      const response = await axios.get(`${API}/banner?business_id=${businessId}`);
      if (response.data.success) {
        setBanner(response.data.banner);
        setOriginalBanner(response.data.banner);
      }
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?business_id=${businessId}`);
      if (response.data.success) {
        setVehicles(response.data.vehicles || []);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const configData = {
        interest_rate: interestRate,
        business_id: businessId,
        phone: phone,
        whatsapp: whatsapp
      };

      const response = await axios.put(`${API}/config`, configData);
      if (response.data.success) {
        setOriginalRate(interestRate);
        setOriginalPhone(phone);
        setOriginalWhatsapp(whatsapp);
        toast.success("Configuración guardada correctamente");
      }
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const saveBanner = async () => {
    setSavingBanner(true);
    try {
      const response = await axios.put(`${API}/banner`, { ...banner, business_id: businessId });
      if (response.data.success) {
        setOriginalBanner(banner);
        toast.success("Banner actualizado correctamente");
      }
    } catch (error) {
      console.error("Error saving banner:", error);
      toast.error("Error al guardar el banner");
    } finally {
      setSavingBanner(false);
    }
  };

  const toggleFeatured = async (vehicleId, currentFeatured) => {
    try {
      const formData = new FormData();
      formData.append("featured", !currentFeatured);
      
      const response = await axios.put(`${API}/vehicles/${vehicleId}/featured`, formData);
      if (response.data.success) {
        // Update local state
        setVehicles(vehicles.map(v => 
          v.id === vehicleId ? { ...v, featured: !currentFeatured } : v
        ));
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Error toggling featured:", error);
      toast.error("Error al actualizar");
    }
  };

  const hasChanges = interestRate !== originalRate || phone !== originalPhone || whatsapp !== originalWhatsapp;
  const hasBannerChanges = originalBanner && (
    banner.enabled !== originalBanner.enabled ||
    banner.title !== originalBanner.title ||
    banner.subtitle !== originalBanner.subtitle ||
    banner.cta_text !== originalBanner.cta_text ||
    banner.background_color !== originalBanner.background_color
  );

  const vehiclesWithPrice = vehicles.filter(v => v.price && v.price !== "");
  const featuredCount = vehicles.filter(v => v.featured).length;

  return (
    <div className="space-y-8">
      {/* Header with business name */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold text-white flex items-center gap-3">
          <Settings className="w-6 h-6" style={{ color: currentBusiness?.accent_color || '#3B82F6' }} />
          Configuración - {currentBusiness?.name || 'HEBEH'}
        </h2>
      </div>
      
      {/* Interest Rate Configuration */}
      <div className="glass-card rounded-lg p-6" data-testid="interest-rate-section">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Percent className="w-5 h-5" style={{ color: currentBusiness?.accent_color || '#3B82F6' }} />
          Configuración del Simulador
        </h2>

        <div className="max-w-md">
          <label className="block text-sm text-slate-400 mb-2">
            Tasa de Interés Anual (%)
          </label>
          <p className="text-xs text-slate-500 mb-4">
            Esta tasa se utilizará en el simulador de financiamiento del sitio público.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                    min="1"
                    max="50"
                    step="0.5"
                    className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white text-xl font-bold focus:border-blue-500 focus:outline-none pr-12"
                    data-testid="interest-rate-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                    <Percent className="w-5 h-5" />
                  </span>
                </div>
                
                <button
                  onClick={saveConfig}
                  disabled={!hasChanges || saving}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                    hasChanges
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-slate-800 text-slate-500 cursor-not-allowed"
                  }`}
                  data-testid="save-config-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : hasChanges ? (
                    <Save className="w-4 h-4" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  {saving ? "Guardando..." : hasChanges ? "Guardar" : "Guardado"}
                </button>
              </div>

              {/* Quick presets */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-slate-500">Preajustes:</span>
                {[15, 18, 20, 22, 25].map((rate) => (
                  <button
                    key={rate}
                    onClick={() => setInterestRate(rate)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      interestRate === rate
                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                        : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                    }`}
                  >
                    {rate}%
                  </button>
                ))}
              </div>

              {hasChanges && (
                <div className="flex items-center gap-2 text-orange-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Tienes cambios sin guardar
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contact Configuration */}
      <div className="glass-card rounded-lg p-6" data-testid="contact-section">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Phone className="w-5 h-5" style={{ color: currentBusiness?.accent_color || '#3B82F6' }} />
          Información de Contacto
        </h2>

        <div className="max-w-lg space-y-6">
          {/* Phone Display */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              Teléfono (para mostrar)
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Este número se muestra en el header y footer del catálogo público.
            </p>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="55 1234 5678"
              className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              data-testid="phone-input"
            />
          </div>

          {/* WhatsApp Number */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              WhatsApp (solo números)
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Este número se usa para los botones de WhatsApp. Incluye código de país sin el +
            </p>
            <div className="relative">
              <input
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, ""))}
                placeholder="5215512345678"
                className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                data-testid="whatsapp-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Ejemplo: 5215512345678 (52 = México, 1 = código de ciudad, resto = número)
            </p>
          </div>

          {/* Preview */}
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-500 mb-2">Vista previa del enlace WhatsApp:</p>
            <a 
              href={`https://api.whatsapp.com/send?phone=${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-400 text-sm hover:underline break-all"
            >
              wa.me/{whatsapp}
            </a>
          </div>

          {/* Save Button for Contact Info */}
          <button
            onClick={saveConfig}
            disabled={!hasChanges || saving}
            className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
              hasChanges
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-slate-800 text-slate-500 cursor-not-allowed"
            }`}
            data-testid="save-contact-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : hasChanges ? (
              <Save className="w-4 h-4" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {saving ? "Guardando..." : hasChanges ? "Guardar Cambios" : "Todo Guardado"}
          </button>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="glass-card rounded-lg p-6" data-testid="notifications-section">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Bell className="w-5 h-5" style={{ color: currentBusiness?.accent_color || '#3B82F6' }} />
          Notificaciones de Leads
        </h2>
        <NotificationSettings businessName={currentBusiness?.name} />
      </div>

      {/* Banner Configuration */}
      <div className="glass-card rounded-lg p-6" data-testid="banner-section">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-orange-500" />
          Banner Promocional
        </h2>

        <div className="space-y-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Mostrar banner</p>
              <p className="text-xs text-slate-500">Activa o desactiva el banner en el catálogo público</p>
            </div>
            <button
              onClick={() => setBanner({ ...banner, enabled: !banner.enabled })}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                banner.enabled
                  ? "bg-green-500/20 text-green-400 border border-green-500/50"
                  : "bg-slate-800 text-slate-400"
              }`}
              data-testid="banner-toggle"
            >
              {banner.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {banner.enabled ? "Activo" : "Inactivo"}
            </button>
          </div>

          {/* Banner Content */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Título</label>
              <input
                type="text"
                value={banner.title}
                onChange={(e) => setBanner({ ...banner, title: e.target.value })}
                placeholder="Ofertas Especiales"
                className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                data-testid="banner-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Texto del botón</label>
              <input
                type="text"
                value={banner.cta_text}
                onChange={(e) => setBanner({ ...banner, cta_text: e.target.value })}
                placeholder="Ver Ofertas"
                className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                data-testid="banner-cta-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Subtítulo</label>
            <input
              type="text"
              value={banner.subtitle}
              onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })}
              placeholder="Encuentra tu vehículo ideal..."
              className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              data-testid="banner-subtitle-input"
            />
          </div>

          {/* Color Scheme */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Estilo de fondo</label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "gradient", label: "Gradiente", preview: "bg-gradient-to-r from-blue-500 to-orange-500" },
                { id: "blue", label: "Azul", preview: "bg-blue-600" },
                { id: "orange", label: "Naranja", preview: "bg-orange-500" },
                { id: "dark", label: "Oscuro", preview: "bg-slate-700" }
              ].map((color) => (
                <button
                  key={color.id}
                  onClick={() => setBanner({ ...banner, background_color: color.id })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    banner.background_color === color.id
                      ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900"
                      : ""
                  }`}
                  data-testid={`banner-color-${color.id}`}
                >
                  <div className={`w-6 h-6 rounded ${color.preview}`} />
                  <span className="text-sm text-slate-300">{color.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Save Banner Button */}
          <div className="flex items-center gap-4 pt-4">
            <button
              onClick={saveBanner}
              disabled={!hasBannerChanges || savingBanner}
              className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                hasBannerChanges
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-slate-800 text-slate-500 cursor-not-allowed"
              }`}
              data-testid="save-banner-btn"
            >
              {savingBanner ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : hasBannerChanges ? (
                <Save className="w-4 h-4" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {savingBanner ? "Guardando..." : hasBannerChanges ? "Guardar Banner" : "Guardado"}
            </button>
            {hasBannerChanges && (
              <span className="text-orange-400 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Cambios sin guardar
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Featured Vehicles */}
      <div className="glass-card rounded-lg p-6" data-testid="featured-vehicles-section">
        <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Vehículos Destacados
          <span className="ml-2 text-sm font-normal text-slate-500">
            ({featuredCount} de {vehiclesWithPrice.length})
          </span>
        </h2>
        <p className="text-slate-500 text-sm mb-6">
          Los vehículos destacados aparecerán en una sección especial en el catálogo público.
          Solo puedes destacar vehículos que tengan precio asignado.
        </p>

        {loadingVehicles ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : vehiclesWithPrice.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehiclesWithPrice.map((vehicle) => (
              <div 
                key={vehicle.id}
                className={`p-4 rounded-lg border transition-all ${
                  vehicle.featured
                    ? "bg-orange-500/10 border-orange-500/50"
                    : "bg-slate-800/50 border-slate-700"
                }`}
                data-testid={`featured-toggle-${vehicle.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-16 h-12 rounded bg-slate-700 flex-shrink-0 overflow-hidden">
                    {vehicle.vehicle_image ? (
                      <img 
                        src={vehicle.vehicle_image} 
                        alt={vehicle.model_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-slate-500 text-xs">Sin img</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {vehicle.model_name}
                    </p>
                    <p className="text-slate-400 text-sm truncate">
                      {vehicle.brand} • {vehicle.version}
                    </p>
                    <p className="text-blue-400 text-sm font-medium">
                      ${vehicle.price}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleFeatured(vehicle.id, vehicle.featured)}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      vehicle.featured
                        ? "bg-orange-500 text-white"
                        : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                    }`}
                  >
                    <Star className={`w-4 h-4 ${vehicle.featured ? "fill-current" : ""}`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Star className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">No hay vehículos con precio</p>
            <p className="text-slate-500 text-sm">
              Agrega un precio a los vehículos desde la pestaña "Diseño" para poder destacarlos.
            </p>
          </div>
        )}
      </div>

      {/* Site Preview Link */}
      <div className="glass-card rounded-lg p-6">
        <h3 className="text-white font-medium mb-2">Vista previa del sitio público</h3>
        <p className="text-slate-400 text-sm mb-4">
          Este es el enlace al catálogo público de {currentBusiness?.name || 'HEBEH'} con el simulador de financiamiento.
        </p>
        <a
          href={`/catalogo/${businessId || 'automotriz'}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
          data-testid="preview-site-link"
        >
          Abrir catálogo público →
        </a>
      </div>
    </div>
  );
}
